import { useEffect, useState } from "react";
import api, { describeError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { History as HistoryIcon, Loader2, Undo2, Plus, Pencil, Trash2, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const OP_STYLE = {
  insert: { icon: Plus, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  insert_many: { icon: Plus, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  update: { icon: Pencil, cls: "text-amber-600 bg-amber-50 border-amber-200" },
  update_many: { icon: Pencil, cls: "text-amber-600 bg-amber-50 border-amber-200" },
  delete: { icon: Trash2, cls: "text-red-600 bg-red-50 border-red-200" },
  delete_many: { icon: Trash2, cls: "text-red-600 bg-red-50 border-red-200" },
};

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Aujourd'hui";
  if (same(d, yest)) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
}

export default function History() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/audit")
      .then((r) => setEntries(r.data))
      .catch((e) => toast.error(describeError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => document.removeEventListener("visibilitychange", onFocus);
  }, []);

  const doRestore = async (entry) => {
    setRestoring(entry.id);
    setConfirm(null);
    try {
      await api.post(`/audit/${entry.id}/restore`);
      toast.success("Restauré avec succès");
      load();
    } catch (e) { toast.error(describeError(e)); } finally { setRestoring(null); }
  };

  // Regroupe par jour
  const groups = [];
  let current = null;
  for (const e of entries) {
    const label = dayLabel(e.created_at);
    if (!current || current.label !== label) { current = { label, items: [] }; groups.push(current); }
    current.items.push(e);
  }

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <HistoryIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl">Historique</h1>
          <p className="text-sm text-muted-foreground">
            Toutes les modifications des dernières 48 h. Restaurez un élément si vous l'avez modifié ou supprimé par erreur.
          </p>
        </div>
        <Button variant="outline" size="sm" className="ml-auto gap-1.5 shrink-0" onClick={load} disabled={loading} data-testid="history-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="h-60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : entries.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="history-empty">
          <HistoryIcon className="w-8 h-8 mx-auto mb-3 opacity-40" />
          Aucune activité enregistrée pour l'instant.
        </Card>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 capitalize">{g.label}</div>
              <div className="space-y-2">
                {g.items.map((e) => {
                  const style = OP_STYLE[e.operation] || OP_STYLE.update;
                  const Icon = style.icon;
                  const canRestore = !e.restored;
                  return (
                    <Card key={e.id} className="p-3 sm:p-4 flex items-center gap-3" data-testid={`history-row-${e.id}`}>
                      <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${style.cls}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">
                          {e.operation_label} · {e.description}
                          {e.n > 1 && <span className="text-muted-foreground font-normal"> ({e.n} éléments)</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {e.collection_label} · {relativeTime(e.created_at)}
                        </div>
                      </div>
                      {e.restored ? (
                        <span className="text-xs text-emerald-600 flex items-center gap-1 shrink-0" data-testid={`history-restored-${e.id}`}>
                          <CheckCircle2 className="w-4 h-4" /> Restauré
                        </span>
                      ) : (
                        <Button
                          variant="outline" size="sm" className="shrink-0 gap-1.5"
                          disabled={!canRestore || restoring === e.id}
                          onClick={() => setConfirm(e)}
                          data-testid={`history-restore-${e.id}`}
                        >
                          {restoring === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                          Restaurer
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="history-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurer cette action ?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm && (
                <>
                  {confirm.operation === "delete" || confirm.operation === "delete_many"
                    ? "L'élément supprimé sera recréé tel qu'il était."
                    : confirm.operation === "insert" || confirm.operation === "insert_many"
                    ? "L'élément créé sera supprimé (annulation de la création)."
                    : "L'élément sera remis dans son état précédent, en annulant cette modification."}
                  <span className="block mt-2 font-medium text-foreground">{confirm.description}</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="history-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => doRestore(confirm)} data-testid="history-confirm-restore">Restaurer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
